import React, { memo, useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, IconButton } from '@one-thd/sui-atomic-components';
import { More } from '@one-thd/sui-icons';
import { RowActionMenuAction } from '../types';

export interface RowActionMenuProps {
  actions: RowActionMenuAction[];
  label?: string;
}

const RowActionMenuComponent: React.FC<RowActionMenuProps> = memo(
  ({ actions, label = 'Row actions' }: RowActionMenuProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('row-action-menu.ready');
    }, []);

    // SUI's Menu calls @floating-ui/react-dom's useFloating() unconditionally, even while closed
    // — under SSR that throws (React error #321: invalid hook call) and takes down the page.
    // Deferring Menu's mount until after client-side render keeps that hook out of SSR; the
    // trigger button renders normally either way, since the menu is invisible until opened.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = () => setIsOpen(false);

    // Rows embedding this menu may themselves be clickable (e.g. ActiveOrdersTable's row-level
    // navigation) — stop propagation so opening the menu or picking an action never also fires
    // the row's click handler.
    const handleTriggerClick = (event: React.SyntheticEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setIsOpen(true);
    };

    const handleSelect = (event: React.SyntheticEvent, action: RowActionMenuAction) => {
      event.stopPropagation();
      action.onSelect();
      handleClose();
    };

    return (
      <>
        <IconButton
          ref={triggerRef}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleTriggerClick}
          icon={More}
        />
        {hasMounted && (
          <Menu open={isOpen} anchorEl={triggerRef.current} onClose={handleClose}>
            {actions.map((action) =>
              action.href ? (
                <MenuItem
                  key={action.key}
                  component="a"
                  href={action.href}
                  onClick={(event: React.SyntheticEvent) => handleSelect(event, action)}
                >
                  <span className="sui-text-sm">{action.label}</span>
                </MenuItem>
              ) : (
                <MenuItem key={action.key} onClick={(event: React.SyntheticEvent) => handleSelect(event, action)}>
                  <span className="sui-text-sm">{action.label}</span>
                </MenuItem>
              )
            )}
          </Menu>
        )}
      </>
    );
  }
);

RowActionMenuComponent.displayName = 'RowActionMenu';

const RowActionMenu = RowActionMenuComponent;

export { RowActionMenu };
